/**
 * The WS-2 job register's columns and card (/well-servicing/jobs).
 *
 * The status rail and badge come from WS_JOB_STATUS_SKIN with the API's label;
 * an unknown status is the slate badge carrying the raw value. Dates are
 * calendar days. Counts are the API's; absent reads as `—`.
 */
import React from 'react';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { wsDay, wsJobStatusSkin } from '../../lib/well-servicing';
import type { WsJob } from '../../types/well-servicing';
import { WsBadge, type WsColumn } from './WsBits';

export const plannedRange = (from: string | null, to: string | null): string =>
  !from && !to ? '—' : `${wsDay(from)}${to ? ` → ${wsDay(to)}` : ''}`;

/** The scopes as the API labels them, one line. */
export const scopeText = (job: Pick<WsJob, 'scopes' | 'scopeLabels'>): string => {
  const labels = (job.scopes ?? []).map((s, i) => job.scopeLabels?.[i] || s).filter(Boolean);
  return labels.length ? labels.join(', ') : '—';
};

export function jobColumns(): WsColumn<WsJob>[] {
  return [
    {
      key: 'number',
      header: 'Job',
      render: (j) => (
        <>
          <p className="whitespace-nowrap font-mono font-extrabold text-white">{j.jobNumber || '—'}</p>
          <p className="mt-0.5 min-w-[140px] text-xs text-fieldiq-muted">{scopeText(j)}</p>
        </>
      ),
    },
    { key: 'customer', header: 'Customer', render: (j) => j.customerName || '—' },
    {
      key: 'well',
      header: 'Well',
      render: (j) => (
        <>
          <p className="min-w-[170px]">{j.wellName || '—'}</p>
          <p className="mt-0.5 whitespace-nowrap font-mono text-xs text-fieldiq-muted">{j.wellUwi || j.wellApiNumber || '—'}</p>
        </>
      ),
    },
    { key: 'rig', header: 'Rig', render: (j) => <span className="whitespace-nowrap font-mono">{j.rigNumber || '—'}</span> },
    { key: 'planned', header: 'Planned', render: (j) => <span className="block min-w-[120px]">{plannedRange(j.plannedStartOn, j.plannedEndOn)}</span> },
    {
      key: 'tours',
      header: 'Tour sheets',
      render: (j) => (
        <>
          <p>{displayValue(j.tourCount ?? undefined)}</p>
          <p className="mt-0.5 whitespace-nowrap text-xs text-fieldiq-muted">{j.lastTourDate ? `Last ${wsDay(j.lastTourDate)}` : '—'}</p>
        </>
      ),
    },
    { key: 'status', header: 'Status', render: (j) => <WsBadge skin={wsJobStatusSkin(j.status, j.statusLabel)} value={j.status} /> },
  ];
}

export function JobCard({ job }: { job: WsJob }) {
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-mono text-sm font-extrabold text-white">{job.jobNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            {job.customerName || '—'} · {job.wellName || '—'}
          </p>
        </div>
        <WsBadge skin={wsJobStatusSkin(job.status, job.statusLabel)} value={job.status} />
      </div>
      <p className="mt-2 text-xs text-fieldiq-text">
        {job.rigNumber || '—'} · {plannedRange(job.plannedStartOn, job.plannedEndOn)} · Tour sheets{' '}
        {displayValue(job.tourCount ?? undefined)}
      </p>
    </>
  );
}
